import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { FileSignature, AlertCircle, Download, CheckCircle, X } from "lucide-react";

import { API } from "@/lib/api-base";
import SignatureCanvas from "@/components/SignatureCanvas";

// Wraps children. If the current user is a doctor with an unsigned mandatory agreement,
// renders a full-screen modal that blocks all navigation until they sign.
export function MandatoryAgreementGate({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);  // {required, signed, ...}
  const [signing, setSigning] = useState(false);
  const [drewSignature, setDrewSignature] = useState(false);
  const [medicalReg, setMedicalReg] = useState("");
  const [panCardNumber, setPanCardNumber] = useState("");
  const [bankingValues, setBankingValues] = useState({});
  const [panCardFile, setPanCardFile] = useState(null);
  const [panCardDataUrl, setPanCardDataUrl] = useState("");
  const [error, setError] = useState("");
  const cvsRef = useRef(null);
  const isDrawing = useRef(false);

  const isDoctor = user && (user.role === "doctor" || user.role === "investigator");

  // Phase 2.44.2 — auto-capture the doctor's medical registration number from
  // their profile (editable; they can override if it changed). We try both
  // the AuthContext user (initial mount) AND the agreement-status response
  // (which is the freshest source if the profile was updated after login).
  // Phase 2.44.4 / 2.46.170 — auto-fill Medical Registration Number
  // from the user profile if we already know it. Doctor can still
  // edit before signing. Checks BOTH `medical_registration_number`
  // (post-first-signature) AND `med_reg_number` (investigator-config
  // alias used by admins when adding the doctor) so first-time
  // signers get the value pre-filled too.
  useEffect(() => {
    const val = user?.medical_registration_number || user?.med_reg_number;
    if (val && !medicalReg) setMedicalReg(val);
  }, [user?.medical_registration_number, user?.med_reg_number]); // eslint-disable-line

  useEffect(() => {
    if (status?.user_medical_registration_number && !medicalReg) {
      setMedicalReg(status.user_medical_registration_number);
    }
  }, [status?.user_medical_registration_number]); // eslint-disable-line

  // Phase 2.46.109 / 2.46.170 — mirror the same pattern for PAN.
  useEffect(() => {
    const val = user?.pan_card_number || user?.pan_card;
    if (val && !panCardNumber) setPanCardNumber(val);
  }, [user?.pan_card_number, user?.pan_card]); // eslint-disable-line
  useEffect(() => {
    if (status?.user_pan_card_number && !panCardNumber) {
      setPanCardNumber(status.user_pan_card_number);
    }
  }, [status?.user_pan_card_number]); // eslint-disable-line

  // Phase 2.46.109 / 2.46.170 — PAN pre-fill handled by the two
  // useEffects above (user + status), which cover both the canonical
  // `pan_card_number` field and the investigator-config `pan_card`
  // alias so first-time doctors see their PAN pre-filled.

  useEffect(() => {
    if (!isDoctor) { setStatus({ required: false, signed: true }); return; }
    let cancelled = false;
    axios.get(`${API}/auth/agreement-status`, { withCredentials: true })
      .then(r => !cancelled && setStatus(r.data))
      .catch(() => !cancelled && setStatus({ required: false, signed: true }));
    return () => { cancelled = true; };
  }, [isDoctor, user?._id]);

  if (!status) return children;
  if (!status.required || status.signed) return children;

  // Canvas helpers are now encapsulated in <SignatureCanvas/> (mobile-robust,
  // non-passive touch listeners). cvsRef exposes .toDataURL() and .clear().

  const onPanCardChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { setError("PAN card image must be ≤ 5 MB"); return; }
    const fr = new FileReader();
    fr.onload = () => {
      setPanCardFile(f);
      setPanCardDataUrl(String(fr.result));
    };
    fr.readAsDataURL(f);
  };

  const submit = async () => {
    if (!drewSignature) { setError("Please draw your signature first."); return; }
    if (status.requires_pan_card && !panCardDataUrl) {
      setError("Please upload your PAN card image.");
      return;
    }
    // Phase 2.46.109 — Zydus also requires the PAN NUMBER (text) so the
    // generated agreement stamps the correct PAN on the doctor copy.
    if (status.requires_pan_card && !panCardNumber.trim()) {
      setError("Please enter your PAN card number.");
      return;
    }
    setSigning(true); setError("");
    try {
      const dataUrl = cvsRef.current.toDataURL("image/png");
      await axios.post(`${API}/studies/${status.study_id}/agreement/sign`, {
        signature_data: dataUrl,
        medical_registration_number: medicalReg || null,
        banking_details: bankingValues,
        document_uploads: {},
        pan_card_data: panCardDataUrl || null,
        pan_card_number: panCardNumber.trim() || null,
      }, { withCredentials: true });
      // Re-check status — gate will dismiss
      const r = await axios.get(`${API}/auth/agreement-status`, { withCredentials: true });
      setStatus(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Submission failed");
    } finally { setSigning(false); }
  };

  const templateUrl = status.has_template
    ? `${API}/studies/${status.study_id}/agreement/template/file`
    : null;
  // Phase 2.46.109 — Don't preview the full agreement template to the
  // doctor when the study is Zydus-style.  The Zydus template is the
  // sponsor-controlled boilerplate and the doctor only needs the
  // gate-form (PAN / MCR / banking / signature) — showing the raw PDF
  // is noisy and clinically irrelevant to the doctor.
  const isZydus = status.style === "zydus" || status.requires_pan_card;
  const showTemplatePreview = templateUrl && !isZydus;

  return (
    <>
      <div className="fixed inset-0 z-[1000] flex items-start justify-center p-4 overflow-y-auto"
           style={{ background: "rgba(5,12,26,0.92)", backdropFilter: "blur(6px)" }}
           data-testid="mandatory-agreement-gate">
        <div className="w-full max-w-3xl my-8">
          <div className="rounded-2xl p-6 sm:p-8" style={{ background: "rgba(13,31,60,0.98)", border: "1px solid rgba(0,180,216,0.3)" }}>
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle size={22} color="#F59E0B" className="flex-shrink-0 mt-1" />
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-semibold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {status.title || "Doctor Agreement & Consent"}
                </h2>
                <p className="text-sm text-slate-400 mt-1">
                  Welcome to <span className="text-white">{status.study_name}</span>. Please read and sign the agreement below to continue.
                  This is mandatory — you cannot access the study workspace until the agreement is signed.
                </p>
              </div>
            </div>

            {showTemplatePreview ? (
              <div className="rounded-lg overflow-hidden mb-4" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
                <object data={templateUrl} type="application/pdf" width="100%" height="380"
                        data-testid="gate-template-preview">
                  <a href={templateUrl} target="_blank" rel="noreferrer"
                     className="block p-4 text-center text-[#00B4D8] text-sm">
                    <Download size={14} className="inline mr-1" /> Open agreement template
                  </a>
                </object>
              </div>
            ) : !isZydus ? (
              <div className="rounded-lg p-4 mb-4 max-h-60 overflow-y-auto"
                   style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
                   data-testid="gate-consent-text">
                <p className="text-sm text-slate-200 whitespace-pre-wrap">
                  {status.consent_text || "I, the undersigned investigator, agree to conduct this study in accordance with the protocol, ICH-GCP guidelines and 21 CFR Part 11."}
                </p>
              </div>
            ) : null}

            {/* Medical registration number */}
            <div className="mb-4">
              <label className="text-xs text-slate-400 uppercase tracking-widest mb-1 block">
                Medical Registration Number
              </label>
              <input value={medicalReg} onChange={e => setMedicalReg(e.target.value)}
                     placeholder="State / National medical council registration number"
                     data-testid="gate-medical-registration-input"
                     className="amdiris-input w-full px-3 py-2 text-sm" />
            </div>

            {/* Phase 2.46.109 — Zydus: PAN card number text input (in addition
                to the PAN card image upload).  Editable on first login. */}
            {isZydus && (
              <div className="mb-4">
                <label className="text-xs text-slate-400 uppercase tracking-widest mb-1 block">
                  PAN Card Number <span className="text-red-400">*</span>
                </label>
                <input value={panCardNumber}
                       onChange={e => setPanCardNumber(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10))}
                       placeholder="ABCDE1234F"
                       maxLength={10}
                       data-testid="gate-pan-card-number-input"
                       className="amdiris-input w-full px-3 py-2 text-sm font-mono tracking-wider" />
                <p className="text-[10px] text-slate-600 mt-0.5">
                  10-character alphanumeric PAN. You can correct this before signing.
                </p>
              </div>
            )}

            {/* Banking details if collected */}
            {status.collect_banking && (status.banking_fields || []).filter(f => f.enabled).length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-slate-400 uppercase tracking-widest mb-2">Banking Details (for honorarium)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(status.banking_fields || []).filter(f => f.enabled).map(f => {
                    // Phase 2.44.2 — strict input validation per field type
                    // (a) Account-holder name: letters + spaces + a few punctuation only
                    // (b) Account number: digits only
                    const isHolder = /holder|name/i.test(f.key);
                    const isAccountNo = /account[_\s-]?(no|number)|bank_account_no/i.test(f.key);
                    const sanitize = (raw) => {
                      if (isHolder) return raw.replace(/[^A-Za-z .'-]/g, "");
                      if (isAccountNo) return raw.replace(/\D/g, "");
                      return raw;
                    };
                    return (
                      <div key={f.key}>
                        <label className="text-[11px] text-slate-500 mb-1 block">
                          {f.label}{f.required && <span className="text-red-400"> *</span>}
                        </label>
                        <input value={bankingValues[f.key] || ""}
                               onChange={e => setBankingValues(b => ({ ...b, [f.key]: sanitize(e.target.value) }))}
                               inputMode={isAccountNo ? "numeric" : undefined}
                               pattern={isAccountNo ? "[0-9]*" : (isHolder ? "[A-Za-z .'-]*" : undefined)}
                               data-testid={`gate-banking-${f.key}-input`}
                               className="amdiris-input w-full px-3 py-2 text-sm" />
                        {isHolder && (
                          <p className="text-[10px] text-slate-600 mt-0.5">Letters only (no digits / symbols).</p>
                        )}
                        {isAccountNo && (
                          <p className="text-[10px] text-slate-600 mt-0.5">Numeric only.</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* PAN card upload — Zydus style only */}
            {status.requires_pan_card && (
              <div className="mb-3" data-testid="gate-pan-section">
                <p className="text-xs text-slate-400 uppercase tracking-widest mb-1.5">
                  PAN Card Image <span className="text-red-400">*</span>
                </p>
                <p className="text-[11px] text-slate-500 mb-2">
                  A clear photo or scan. Will be embedded on the final agreement page.
                </p>
                <input type="file" accept="image/*" onChange={onPanCardChange}
                       data-testid="gate-pan-input"
                       className="text-xs text-slate-300 file:mr-3 file:px-3 file:py-1.5 file:rounded-md
                                  file:border-0 file:bg-[#00B4D8]/15 file:text-[#00B4D8]
                                  hover:file:bg-[#00B4D8]/25 cursor-pointer" />
                {panCardDataUrl && (
                  <div className="mt-2 inline-block rounded-lg overflow-hidden border border-white/10"
                       data-testid="gate-pan-preview">
                    <img src={panCardDataUrl} alt="PAN preview" className="max-h-32" />
                  </div>
                )}
              </div>
            )}

            {/* Signature canvas — mobile-robust */}
            <div className="mb-3">
              <p className="text-xs text-slate-400 uppercase tracking-widest mb-1.5">
                Investigator Signature <span className="text-red-400">*</span>
              </p>
              <SignatureCanvas ref={cvsRef} width={680} height={140}
                               data-testid="gate-signature-canvas"
                               onDrawn={() => setDrewSignature(true)} />
              <div className="flex justify-end mt-2">
                <button onClick={() => { cvsRef.current?.clear(); setDrewSignature(false); }}
                        className="btn-secondary text-xs px-3 py-1.5"
                        data-testid="gate-clear-signature-btn">Clear</button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-400 mb-3" data-testid="gate-error">{error}</p>
            )}

            <button onClick={submit} disabled={signing}
                    data-testid="gate-submit-btn"
                    className="btn-primary text-sm w-full py-3 flex items-center justify-center gap-2">
              <FileSignature size={14} />
              {signing ? "Submitting..." : "Sign & Enter Study"}
            </button>
          </div>
        </div>
      </div>
      {/* Children rendered behind the modal but inert */}
      <div aria-hidden style={{ filter: "blur(4px)", pointerEvents: "none" }}>{children}</div>
    </>
  );
}

// ==================== NOTIFICATION BELL ====================
export function NotificationBell() {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const btnRef = useRef(null);

  const refreshCount = async () => {
    try {
      const r = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      setUnread(r.data?.count || 0);
    } catch { /* noop */ }
  };

  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, 30000);
    return () => clearInterval(id);
  }, []);

  // Re-measure anchor on open + on window resize / scroll so dropdown tracks the bell.
  useEffect(() => {
    if (!open) return;
    const measure = () => {
      if (btnRef.current) setAnchorRect(btnRef.current.getBoundingClientRect());
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open]);

  const loadList = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/notifications`, { withCredentials: true });
      setItems(r.data || []);
    } finally { setLoading(false); }
  };

  const toggle = async () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen) await loadList();
  };

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, null, { withCredentials: true });
      setItems(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
      refreshCount();
    } catch { /* noop */ }
  };

  const markAll = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, null, { withCredentials: true });
      setItems(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* noop */ }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear all notifications? They will be hidden from your inbox.")) return;
    try {
      await axios.delete(`${API}/notifications/clear-all`, { withCredentials: true });
      setItems([]);
      setUnread(0);
    } catch { /* noop */ }
  };

  // Dropdown rendered via portal to document.body so it escapes every parent
  // stacking context (header, main content, modals, etc.) — guarantees no
  // page content can overlay it regardless of role/layout.
  // Note: we deliberately AVOID z-index 2147483647 because App.css uses that
  // exact value to hide the "Made with Emergent" badge — using it on our own
  // dropdown would also hide it. 999999999 is safely above every app layer.
  const dropdown = open && anchorRect ? createPortal(
    <>
      <div className="fixed inset-0" style={{ zIndex: 999999998 }}
           onClick={() => setOpen(false)} />
      <div className="w-[360px] rounded-xl overflow-hidden shadow-2xl"
           data-testid="notification-dropdown"
           style={{
             position: "fixed",
             zIndex: 999999999,
             top: Math.min(anchorRect.bottom + 8, window.innerHeight - 460),
             right: Math.max(window.innerWidth - anchorRect.right, 12),
             background: "#0D1F3C",
             border: "1px solid rgba(0,180,216,0.35)",
             boxShadow: "0 20px 60px rgba(0,0,0,0.65)",
           }}>
        <div className="flex items-center justify-between px-4 py-3"
             style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <p className="text-sm font-semibold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Notifications</p>
          <div className="flex items-center gap-3">
            <button onClick={markAll} className="text-[11px] text-[#00B4D8] hover:text-white"
                    data-testid="notification-mark-all-read-btn">
              Mark all read
            </button>
            <span className="text-slate-700">·</span>
            <button onClick={clearAll}
                    className="text-[11px] text-red-400 hover:text-red-300"
                    data-testid="notification-clear-all-btn">
              Clear all
            </button>
            <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white">
              <X size={14} />
            </button>
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {loading ? (
            <p className="text-xs text-slate-500 text-center py-6">Loading…</p>
          ) : items.length === 0 ? (
            <div className="text-center py-10 px-4">
              <CheckCircle size={28} className="mx-auto text-slate-600 mb-2" />
              <p className="text-sm text-slate-400">You're all caught up.</p>
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
              {items.map(n => (
                <li key={n._id} className="px-4 py-3 hover:bg-white/[0.03] transition-colors"
                    data-testid={`notification-item-${n._id}`}>
                  <div className="flex items-start gap-3">
                    <span className={`w-2 h-2 mt-1.5 rounded-full flex-shrink-0 ${n.read ? "bg-slate-700" : "bg-[#00B4D8]"}`} />
                    <div className="flex-1 min-w-0" onClick={() => !n.read && markRead(n._id)}>
                      <p className={`text-sm ${n.read ? "text-slate-400" : "text-white"} truncate`}>{n.title}</p>
                      {(n.body || n.message) && (
                        <p className="text-[11px] text-slate-500 line-clamp-2 mt-0.5">{n.body || n.message}</p>
                      )}
                      <p className="text-[10px] text-slate-600 mt-1">
                        {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
                      </p>
                    </div>
                    {n.link && (
                      <a href={n.link} className="text-[11px] text-[#00B4D8] hover:text-white whitespace-nowrap"
                         onClick={() => markRead(n._id)}>
                        View →
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>,
    document.body
  ) : null;

  return (
    <div className="relative">
      <button ref={btnRef} onClick={toggle}
              data-testid="notification-bell-btn"
              className="relative p-2 rounded-lg text-slate-400 hover:text-white transition-colors"
              style={{ background: "rgba(255,255,255,0.05)" }}>
        <BellIcon />
        {unread > 0 && (
          <span data-testid="notification-unread-count"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
                style={{ background: "#EF4444" }}>
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {dropdown}
    </div>
  );
}

function BellIcon() {
  // eslint-disable-next-line
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>;
}
